/**
 * @module data
 * @description Composition root for storage, plus React hooks that subscribe
 * to it. Swap the constructors here to move from demo to production.
 */
import { useSyncExternalStore } from "react";
import {
    BrowserPledgeRepository,
    BrowserSettingsRepository,
    createBrowserCardRepository,
    createBrowserThankYouRepository,
} from "./browserStore";
import type { CardRepository, PledgeRepository, Settings, SettingsRepository, ThankYouRepository } from "./repositories";
import type { OrderedRecord, Pledge, StoryCard, ThankYouCard } from "../domain/types";

export const cardRepository: CardRepository = createBrowserCardRepository();
export const thankYouRepository: ThankYouRepository = createBrowserThankYouRepository();
export const pledgeRepository: PledgeRepository = new BrowserPledgeRepository();
export const settingsRepository: SettingsRepository = new BrowserSettingsRepository();

/** Public views show published records that are not on content hold. */
export const isPublic = (record: OrderedRecord): boolean => record.published && !record.contentHold;

/** All cards, re-rendering on any change (this tab or another). */
export function useCards(): StoryCard[] {
    return useSyncExternalStore(
        (l) => cardRepository.subscribe(l),
        () => cardRepository.list()
    );
}

/** Public cards only. */
export function usePublishedCards(): StoryCard[] {
    return useCards().filter(isPublic);
}

export function useThankYouCards(): ThankYouCard[] {
    return useSyncExternalStore(
        (l) => thankYouRepository.subscribe(l),
        () => thankYouRepository.list()
    );
}

export function usePledges(): Pledge[] {
    return useSyncExternalStore(
        (l) => pledgeRepository.subscribe(l),
        () => pledgeRepository.list()
    );
}

export function useOfflineTotal(): number {
    return useSyncExternalStore(
        (l) => pledgeRepository.subscribe(l),
        () => pledgeRepository.getOfflineTotal()
    );
}

export function useOfflineCount(): number {
    return useSyncExternalStore(
        (l) => pledgeRepository.subscribe(l),
        () => pledgeRepository.getOfflineCount()
    );
}

export function useSettings(): Settings {
    return useSyncExternalStore(
        (l) => settingsRepository.subscribe(l),
        () => settingsRepository.get()
    );
}
